<template>
  <div
    class="contact"
    :class="contactClasses"
    data-testid="outreach-contact"
  >
    <div
      v-if="!hideCheckbox"
      class="contact-selection"
    >
      <BaseCheckbox
        data-testid="outreach-contact-checkbox"
        dense
        :disabled="contactOptedOut && !isOutreachContactListsRoute"
        :model-value="isChecked"
        new-design
        @update:model-value="checkboxClicked($event)"
      />
    </div>

    <OutreachContactAvatar :contact="contact" />

    <div class="contact-details-wrapper">
      <div class="contact-details">
        <div class="contact-header">
          <div class="contact-header-name-wrapper">
            <div class="contact-header-name">
              <BaseIcon
                v-if="isPrivate"
                class="contact-lock"
                icon="lock"
              />
              <div
                v-if="type === 'private_outlet'"
                class="text-bold"
              >
                {{ contact.name }}
              </div>
              <button
                v-else
                class="contact-header-link"
                data-testid="outreach-contact-link"
                :disabled="contactOptedOut"
                type="button"
                @click="nameClicked"
              >
                {{ contactTitle }}
              </button>

              <div
                v-if="!contactOptedOut && !isPrivate && !hidePreview"
                style="display: inline-flex"
              >
                <BaseButton
                  aria-haspopup="dialog"
                  class="contact-preview-button"
                  color="secondary"
                  data-testid="outreach-contact-preview"
                  icon="expand-preview"
                  :label="getLocaleText('outreach_contact.preview')"
                  new-design
                  new-icon
                  size="sm"
                  @click="previewClicked"
                />
              </div>
            </div>

            <OutreachContactOptedOut
              v-if="contactOptedOut"
              :outlet="contactPrimaryOutlet"
            />
          </div>
        </div>

        <div
          v-if="showEmail && visibleContactEmails[0] && !contactOptedOut"
          class="contact-email"
        >
          <QIcon
            name="email"
            size="20px"
          />
          <a :href="`mailto:${visibleContactEmails[0]}`">
            <BaseEllipseContent>
              {{ visibleContactEmails[0] }}
            </BaseEllipseContent>
          </a>
        </div>

        <div class="contact-info">
          <template v-if="!contactOptedOut">
            <div
              v-if="contactOutletType"
              class="contact-stat"
            >
              <span class="contact-stat-outlet-type" />
              {{ contactOutletType }}
            </div>

            <div
              v-if="contactCirculations.length"
              class="contact-stat"
            >
              <QIcon
                name="ion-ios-people"
                size="1rem"
              />
              <span>{{ contactCirculations[0].circulation }}</span>
              <BaseStreemTooltip>
                {{ contactCirculationTooltip }}
              </BaseStreemTooltip>
            </div>
          </template>

          <div
            v-if="contactRole"
            class="contact-stat contact-stat-role"
          >
            <QIcon
              name="far fa-address-card"
              size=".8rem"
            />
            <BaseEllipseContent>
              {{ contactRole }}
            </BaseEllipseContent>
          </div>
          <div
            v-if="contactLocation && !contactOptedOut"
            class="contact-stat contact-stat-location"
          >
            <QIcon
              name="ion-ios-pin"
              size=".8rem"
            />
            <span>{{ contactLocation }}</span>
          </div>
        </div>

        <template v-if="!contactOptedOut">
          <div
            v-if="showCombinedContactOutlets(contact)"
            class="contact-info contact-list-outlets"
          >
            <BaseIcon icon="outlet" />
            <OutreachContactListItems
              :is-item-clickable="(item) => !item.isCustom && Boolean(item.id)"
              :items="combinedContactOutlets"
              :title="getLocaleText('outreach_contact.outlets')"
              @click="outletClicked"
            >
              <template #itemTooltip="{ item }">
                <span v-if="item.isCustom">
                  {{
                    getLocaleText(
                      "outreach_private_influencer_form.outlet_custom_tooltip"
                    )
                  }}
                </span>
              </template>
              <template #itemPopoverTooltip="{ item }">
                <span v-if="item.isCustom">
                  {{
                    getLocaleText(
                      "outreach_private_influencer_form.outlet_custom_tooltip"
                    )
                  }}
                </span>
              </template>
            </OutreachContactListItems>
          </div>

          <div
            v-if="contactTopics.length"
            class="contact-info contact-list-sectors"
          >
            <div class="contact-list-sectors__title">
              {{ getLocaleText("outreach_contact.sectors") }}:
              <BaseStreemTooltip>
                <div class="q-pa-sm">
                  {{ getLocaleText("outreach_contact.sectors_tooltip") }}
                </div>
              </BaseStreemTooltip>
            </div>
            <OutreachContactListItems
              :clickable="clickableTopics"
              :force-tooltip="!hasAdvancedTopics"
              :items="contactTopics"
              :title="getLocaleText('outreach_contact.sectors')"
              @click="topicClicked"
            >
              <template
                v-if="clickableTopics"
                #itemTooltip="{ item, isEllipsed }"
              >
                <template v-if="isEllipsed">
                  {{ getLocaleText("outreach_contact.filter_by") }}
                  {{ item.name }}
                </template>
                <template v-else>
                  {{ getLocaleText("outreach_contact.filter_by_sector") }}
                </template>
              </template>

              <template
                v-if="clickableTopics"
                #itemPopoverTooltip
              >
                {{ getLocaleText("outreach_contact.filter_by_sector") }}
              </template>
            </OutreachContactListItems>
          </div>

          <slot
            :contact="contact"
            name="actions"
            :type="type"
          />
        </template>
      </div>

      <div
        v-if="!hideExtras"
        class="contact-details-extra"
      >
        <template v-if="!contactOptedOut">
          <OutreachContactSocialStats
            v-if="type === 'influencer'"
            :contact="contact"
          />

          <OutreachContactSocials
            v-else
            class="contact-socials"
            :emails="contactEmails"
            :homepages="homepages(contact)"
            :invalid-email="invalidEmail(contact)"
            :phones="contactPhones"
            :socials="socials(contact)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseEllipseContent,
  BaseIcon,
  BaseStreemTooltip,
} from "shared/components/base";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import OutreachContactAvatar from "shared/components/Outreach/OutreachContact/OutreachContactAvatar.vue";
import OutreachContactListItems from "shared/components/Outreach/OutreachContact/OutreachContactListItems.vue";
import OutreachContactOptedOut from "shared/components/Outreach/OutreachContact/OutreachContactOptedOut.vue";
import OutreachContactSocials from "shared/components/Outreach/OutreachContact/OutreachContactSocials.vue";
import OutreachContactSocialStats from "shared/components/Outreach/OutreachContact/OutreachContactSocialStats.vue";
import useContact, {
  type ContactType,
  isInfluencerContact,
  isPrivateInfluencerContact,
} from "shared/composables/useContact";
import { type BaseOutlet, type OutreachTopic } from "shared/types/outreach";

interface CombinedOutletItem {
  id?: number | string;
  isCustom: boolean;
  name: string;
  outlet?: BaseOutlet;
}

interface OutreachContactProps {
  clickableTopics?: boolean;
  contact: ContactType;
  hasAdvancedTopics?: boolean;
  hideCheckbox?: boolean;
  hideExtras?: boolean;
  hidePreview?: boolean;
  isChecked?: boolean;
  isPrivate?: boolean;
  openOutletLinksInNewTab?: boolean;
  showEmail?: boolean;
}

const props = defineProps<OutreachContactProps>();

const emit = defineEmits(["click", "topic", "checkbox-clicked", "preview"]);

const {
  type,
  contactClass,
  contactTitle,
  contactLocation,
  contactOutletType,
  contactRole,
  contactTopics,
  contactCirculations,
  contactEmails,
  visibleContactEmails,
  contactPhones,
  contactCirculationTooltip,
  contactOutlets,
  contactPrivateOutlets,
  contactPrimaryOutlet,
  contactOptedOut,
} = useContact(props);

const combinedContactOutlets = computed<CombinedOutletItem[]>(() => [
  ...contactOutlets.value.map((outlet) => ({
    id: outlet.id,
    isCustom: false as const,
    name: outlet.name,
    outlet,
  })),
  ...contactPrivateOutlets.value.map((privateOutlet) => ({
    id: privateOutlet.id,
    isCustom: true as const,
    name: privateOutlet.name,
  })),
]);

const route = useRoute();
const router = useRouter();

const isOutreachContactListsRoute = computed(
  () => route.name === "outreach-lists-contacts"
);

const contactClasses = computed(() => {
  const items = [contactClass.value, `contact-${type.value}`];

  if (contactOptedOut.value && !isOutreachContactListsRoute.value) {
    items.push("contact-disabled");
  }

  if (props.isChecked) {
    items.push("contact-checked");
  }

  return items;
});

function nameClicked() {
  if (contactOptedOut.value) return;

  if (Number(props.contact.id) === 0) return;

  emit("click", props.contact, type.value);
}

function showCombinedContactOutlets(contact: ContactType) {
  return (
    (isInfluencerContact(contact) || isPrivateInfluencerContact(contact)) &&
    combinedContactOutlets.value.length > 0
  );
}

function topicClicked(topic: OutreachTopic) {
  emit("topic", topic, type.value);
}

function outletClicked(item: CombinedOutletItem) {
  if (item.isCustom || !item.outlet || !item.outlet.id) return;

  if (props.openOutletLinksInNewTab) {
    const resolvedRoute = router.resolve({
      name: "outreach-search-outlet",
      params: { id: item.outlet.id },
    });

    window.open(resolvedRoute.href, "_blank", "noopener,noreferrer");

    return;
  }

  emit("click", item.outlet, "outlet");
}

function checkboxClicked(checked: boolean) {
  emit("checkbox-clicked", props.contact, checked);
}

function previewClicked() {
  emit("preview", props.contact, type.value);
}

function invalidEmail(contact: ContactType) {
  return "invalid_email" in contact ? contact.invalid_email : false;
}

function socials(contact: ContactType) {
  return "socials" in contact ? contact.socials : [];
}

function homepages(contact: ContactType) {
  return "homepages" in contact ? contact.homepages : [];
}
</script>

<style lang="scss" scoped>
@import "shared/css/outreach";

.contact {
  padding-inline: var(--s-spacing-lg);
  padding-block: var(--s-spacing-lg);
  display: flex;
  gap: var(--s-spacing-lg);
  background-color: white;
  position: relative;
  min-height: 72px;

  &-disabled {
    .checkbox,
    .c-outreach-contact-avatar,
    .contact-header-name,
    .contact-info {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &-header-link {
    background: transparent;
    border: 0;
    color: var(--s-color-denim-9);
    cursor: pointer;
    padding: 0;

    &:hover {
      color: var(--s-title-hover);
    }
  }

  &-preview-button {
    font-weight: 500;
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    transition:
      opacity 150ms,
      visibility 0s 150ms;

    --c-button-color: var(--s-color-denim-9);
    --c-button-border-radius: var(--s-border-radius-md);
    --c-button-font-size: var(--s-font-size-md);
    --c-button-padding-inline: 14px;
    --c-button-padding-block: 8px;
  }

  &-selection {
    display: flex;
    align-items: start;
  }

  &-socials {
    opacity: 0;
    transition: 150ms opacity;
  }

  &-details-wrapper {
    display: flex;
    gap: var(--s-spacing-lg);
    flex: 1;
    justify-content: space-between;
  }

  &-details {
    gap: var(--s-spacing-sm);
    display: flex;
    flex-direction: column;
    flex-shrink: 1;
    min-width: 300px;
    justify-content: center;
    overflow: hidden;
  }

  &-details-extra {
    flex-shrink: 0;
  }

  &-header {
    gap: var(--s-spacing-md);
    display: flex;
    justify-content: space-between;
    align-items: start;

    &-name-wrapper {
      display: flex;
      align-items: center;
      gap: var(--s-spacing-md);
    }

    &-name {
      flex: 1;
      min-height: 32px;
      display: flex;
      align-items: center;
      gap: var(--s-spacing-md);

      .contact-header-link {
        color: var(--s-color-blue-9);
        line-height: 150%;
        font-weight: 700;

        &:hover {
          color: var(--s-color-blue-5);
        }
      }
    }

    &-role {
      font-weight: bold;
      color: var(--s-color-denim-7);
    }
  }

  &-lock {
    color: var(--s-color-denim-9);
  }

  &-info {
    gap: var(--s-spacing-md);
    display: flex;
    align-items: center;
  }

  &-email {
    display: flex;
    align-items: center;
    gap: var(--s-spacing-sm);
    min-width: 0;
    max-width: 100%;
    color: var(--s-color-blue-9);

    a {
      display: block;
      color: var(--s-color-blue-9);
      min-width: 0;
      max-width: 100%;
      overflow: hidden;

      &:hover {
        color: var(--s-title-hover);
      }
    }

    :deep(.ellipsis) {
      min-width: 0;
      max-width: 100%;
    }
  }

  &-list-outlets,
  &-list-sectors {
    color: var(--s-color-denim-6);
    gap: var(--s-spacing-sm);
    font-size: var(--s-font-size-md);

    &__title {
      color: var(--s-color-denim-7);
      line-height: 150%;
      text-decoration: dotted underline;
      font-weight: 500;
    }
  }

  &-stat {
    gap: var(--s-spacing-sm);
    display: flex;
    align-items: center;
    color: var(--s-color-denim-6);

    &-outlet {
      overflow: hidden;
    }

    &-role {
      max-width: 200px;
      overflow: hidden;
      padding-left: 1px;
    }

    &-location {
      min-width: fit-content;
    }

    &-outlet-type {
      background-color: var(--c-outreach-contact-color);
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }
  }

  &:hover,
  &:focus-within {
    background-color: var(--s-color-denim-1);

    .contact-socials {
      opacity: 1;
    }

    .contact-preview-button {
      opacity: 1;
      pointer-events: auto;
      visibility: visible;
      transition:
        opacity 150ms,
        visibility 0s;
    }
  }

  &-checked {
    .contact-add-button {
      opacity: 1;
    }
  }
}
</style>
